import { GraphQLError } from 'graphql/index';
import React, { useState } from 'react';
import { graphql, useMutation } from 'react-relay';

import useToast from '../../../hooks/useToast';
import { TaskOriginalBulkUpdateDialog_taskOriginalBulkUpdateMutation } from '../../../relay/__generated__/TaskOriginalBulkUpdateDialog_taskOriginalBulkUpdateMutation.graphql';
import { parseGraphQLError } from '../../../utils/error';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import NumberInput from '../../core/NumberInput';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import TaskOriginalUpdateNoticeList from '../TaskOriginalUpdateNoticeList';

type Props = {
  tasks: string[];
  // 부모를 바꾼 뒤 (목록 새로고침 등)
  onUpdated?: () => void;
} & DialogProps;

const TaskOriginalBulkUpdateDialog = ({ tasks, onUpdated, ...props }: Props) => {
  const { toast } = useToast();
  const [originalTaskSequence, setOriginalTaskSequence] = useState('');

  const [commit, isInFlight] = useMutation<TaskOriginalBulkUpdateDialog_taskOriginalBulkUpdateMutation>(graphql`
    mutation TaskOriginalBulkUpdateDialog_taskOriginalBulkUpdateMutation($input: TaskOriginalBulkUpdateInput!) {
      taskOriginalBulkUpdate(input: $input) {
        id
        sequence
      }
    }
  `);

  const save = () => {
    if (!originalTaskSequence) {
      toast('새 부모 문제 번호를 입력해 주세요', 'error');
      return;
    }
    commit({
      variables: { input: { tasks, originalTaskSequence: Number(originalTaskSequence) } },
      onCompleted: ({ taskOriginalBulkUpdate }) => {
        const republished = taskOriginalBulkUpdate.map(({ sequence }) => sequence);
        toast(
          republished.length > 0
            ? `부모 문항을 바꾸고 ${republished.length}건을 수학대왕에 재출시했어요 (${republished.join(', ')})`
            : '부모 문항을 바꿨어요',
          'success',
        );
        onUpdated?.();
        props.onDismiss?.();
      },
      onError: (error) => {
        toast(parseGraphQLError(error as GraphQLError)?.[0].message ?? '부모 문항을 바꾸지 못했어요', 'error');
      },
    });
  };

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>부모 문항 일괄 변경</Text>
      </Dialog.Header>
      <Dialog.Body>
        <Text fontWeight={'bold'}>새 부모 문제 번호</Text>
        <Stack gapX={2}>
          <Stack.Item>
            <NumberInput
              value={originalTaskSequence}
              onChange={(e) => setOriginalTaskSequence(e.target.value)}
              placeholder={'sequence'}
              skipDebounce
            />
          </Stack.Item>
          <Stack.Item>
            <Text fontSize={1} color={'fg.muted'} sx={{ whiteSpace: 'nowrap' }}>
              유형 대표(원본·2단계) 문제만
            </Text>
          </Stack.Item>
        </Stack>
        <TaskOriginalUpdateNoticeList
          notices={[
            '자식 문항의 변형 단계를 유지하며 새 부모의 자식 문항으로 변경',
            '선택 문항과 자식 문항을 포함하여 제목의 [복제] 태그 수 변경',
            '선택 문항과 자식 문항을 포함하여 출시된 문제를 내게 검수 단계로 배정 후 재출시',
            '마이그레이션 짝 동기화',
          ]}
        />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          variant={'primary'}
          size={'large'}
          disabled={isInFlight}
          onClick={() => save()}
        >
          저장하기
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

export default TaskOriginalBulkUpdateDialog;
